<template>
  <div
    ref='wrap'
    class="multilineinput-wrapper relative flex flex-col"
    :class="{
      'editable': allowEdit,
      'active': isActive,
    }"
  >
    <div
      :class="[TITLE_CLASS, allowEdit ? 'h-fit! cursor-pointer' : '']"
      ref='div'
      v-if='!opened && !render'
      @click='activateIfAllowed()'
    >{{ title }}</div>
    <div
      :class="[TITLE_CLASS, allowEdit ? 'h-fit! cursor-pointer' : '']"
      ref='div'
      v-else-if="!opened && render"
      v-html='renderWithFeatures'
      @click='activateIfAllowed()'
    ></div>
    <Editor
      v-if="opened"
      ref='editorRef'
      :title="title"
      :value="value"
      :typeOfEditor="typeOfEditor"
      :alwaysOpen="alwaysOpen"
      :submitOnFocusOut="submitOnFocusOut"
      :tagList="tagList"
      :reversedTag="reversedTag"
      :allowSubmit="allowSubmit"
      :isHideFormat="isHideFormat"
      :previewImage="previewImage"
      :mentionButton="mentionButton"
      :attachmentButton="attachmentButton"
      :recordingButton="recordingButton"
      :emojiPickerBelow="emojiPickerBelow"
      :notFocusOnInit="notFocusOnInit"
      :dontSubmitOnEnter="dontSubmitOnEnter"
      :maxHeight="maxHeight"
      @click="emitClick()"
      @focus="onEditorFocus"
      @input="emit('input', $event)"
      @clicked='isActive=true'
      @submit="doSubmit"
      @opened="toggleOpened"
      @commentAttachImage="emit('commentAttachImage', $event)"
      @submitWithAttach="emit('submitWithAttach', $event)"
    />
    <UnsavedChangesPrompt
      v-if="opened && showUnsavedPrompt && !alwaysOpen"
      :title="$t ? $t('common.Unsaved_changes') : 'Unsaved changes'"
      :message="$t ? $t('common.There_are_unsaved_changes_in_this_comment') : 'There are unsaved changes in this comment.'"
      :cancel-text="$t ? $t('common.Continue_editing') : 'Continue editing'"
      :confirm-text="$t ? $t('common.Drop_changes') : 'Drop changes'"
      @cancel="onUnsavedCancel"
      @confirm="onUnsavedConfirm"
    />
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onBeforeUnmount, ref, watch } from 'vue';
import mediumZoom from 'medium-zoom';
import Editor from '@/components/elements/Editor';
import UnsavedChangesPrompt from '@/components/elements/UnsavedChangesPrompt.vue';
import 'highlight.js/styles/atom-one-dark.css'; // Import a highlight.js theme for rendering code blocks

// `title` is also a styling hook used by parent components (PinnedComment, Task, Header)
const TITLE_CLASS = 'title flex flex-col border border-transparent p-0.5 text-body text-text';

// Props
const props = defineProps({
  title: String,
  value: [String, Array, Object],
  render: String,
  notFocusOnInit: {
    type: Boolean,
    default: false,
  },
  reversedTag: {
    type: Boolean,
    default: false,
  },
  maxHeight: {
    type: String,  
  },
  dontSubmitOnEnter: {
    type: Boolean,
    default: false,
  },
  allowEdit: {
    type: Boolean,
    default: true,
  },
  alwaysOpen: {
    // some editors can be never closed - e.g. in task
    type: Boolean,
    default: false,
  },
  submitOnFocusOut: {
    type: Boolean,
    default: false,
  },
  tagList: {
    type: Array,
    default: () => [],
  },
  allowSubmit: {
    type: Boolean,
    default: true,
  },
  needAdjustHeight: {
    type: Boolean,
    default: true,
  },
  isHideFormat: {
    type: Boolean,
    default: false,
  },
  typeOfEditor: {
    type: String,
    default: 'main',
  },
  previewImage: {
    // Using any here; keeping parity with previous implementation
    default: () => {},
  },
  mentionButton: {
    type: Boolean,
    default: true,
  },
  attachmentButton: {
    type: Boolean,
    default: true,
  },
  recordingButton: {
    type: Boolean,
    default: true,
  },
  emojiPickerBelow: {
    type: Boolean,
    default: false,
  },
});

// Emits
const emit = defineEmits([
  'input',
  'commentAttachImage',
  'submitWithAttach',
  'clicked',
  'submit',
  'update:opened',
  'needScroll',
  'blur',
]);

// Refs
const wrap = ref(null);
const div = ref(null);
const editorRef = ref(null);

// State
const isActive = ref(false);
const opened = ref(false);
const showUnsavedPrompt = ref(false);
let pendingOutsideEvent = null;
let outsideGuard = null;
let pointerDownStartedOutside = false; // track where the interaction started

// Computed
const renderWithFeatures = computed(() => {
  const html = props.render || props.title || '';
  const regex = /#([0-9a-fA-F]{6})/g;
  const replacedHtml = html.replace(
    regex,
    (_, color) => `<span class="color-square" style="background-color: #${color}"></span> #${color}`,
  );
  return replacedHtml;
});

// Methods
function unfocusEditor() {
  editorRef.value?.doBlur();
}

function createZoom() {
  nextTick(() => {
    if (!div.value) return;
    // Apply zoom to all images except those explicitly opted-out via data-nozoom
    const imgs = div.value.querySelectorAll('img:not([data-nozoom])');
    if (!imgs.length) return;
    mediumZoom(imgs, {
      background: 'rgba(0,0,0,0.7)',
      margin: 30,
    });
  });
}

async function activateIfAllowed() {
  if (!props.allowEdit) {
    return;
  }
  opened.value = true;
  // Place cursor at the end after editor is rendered
  await nextTick();
  editorRef.value.focusToEnd();
}

function doSubmit(event) {
  if (!event.text) return;
  isActive.value = false;
  emit('submit', event);
  if (!props.alwaysOpen) {
    opened.value = false;
  }
}

function emitClick() {
  emit('clicked');
}

function adjustHeight() {
  nextTick(() => {
    if (div.value) {
      const { scrollHeight } = div.value;
      if (!scrollHeight) return;
      const height = Math.ceil(scrollHeight);
      setTimeout(() => {
        if (div.value) div.value.style.height = `${height}px`;
      });
    }
  });
}

function toggleOpened(state) {
  if (props.alwaysOpen && !state) {
    return;
  }
  opened.value = state;
  emit('update:opened', opened.value);
  // When editor closes, treat it as a blur for parent consumers
  if (!opened.value) emit('blur');
}

// Forward focus changes from inner Editor to expose a blur event
function onEditorFocus(val) {
  if (val === false && !wrap.value.contains(document.activeElement)) emit('blur');
}

// Lifecycle
onMounted(() => {
  if (props.alwaysOpen) {
    opened.value = true;
  }
  if (props.render) {
    nextTick(() => createZoom());
  }

  setTimeout(() => {
    if (div.value && wrap.value && wrap.value.offsetHeight < div.value.offsetHeight) {
      emit('needScroll', true);
    } else {
      emit('needScroll', false);
    }
    if (props.needAdjustHeight) {
      adjustHeight();
    }
  });

  bindGlobalOutsideGuards();
});

onBeforeUnmount(() => {
  unbindGlobalOutsideGuards();
});

// Watches
watch(opened, (val) => {
  emit('update:opened', val);
  if (props.needAdjustHeight) {
    adjustHeight();
  }
});

watch(
  () => props.render,
  () => {
    nextTick(() => createZoom());
  },
);

// Expose methods for parent refs (used in Checklist.vue etc.)
defineExpose({
  activateIfAllowed,
  unfocusEditor,
});

// --- Unsaved changes and outside click handling ---
function clonePointerEvent(e) {
  return {
    type: e.type,
    target: e.target,
    clientX: e.clientX,
    clientY: e.clientY,
    button: e.button,
    altKey: e.altKey,
    ctrlKey: e.ctrlKey,
    metaKey: e.metaKey,
    shiftKey: e.shiftKey,
  };
}

function resumePendingOutsideEvent() {
  if (!pendingOutsideEvent) return;
  const { target, type, clientX, clientY, button, altKey, ctrlKey, metaKey, shiftKey } = pendingOutsideEvent;
  const evt = new MouseEvent(type || 'click', {
    bubbles: true,
    cancelable: true,
    view: window,
    clientX, clientY,
    button,
    altKey, ctrlKey, metaKey, shiftKey,
  });
  target.dispatchEvent(evt);
  pendingOutsideEvent = null;
}

function onUnsavedCancel() {
  showUnsavedPrompt.value = false;
  // keep focus inside editor
  editorRef.value.doFocus();
}

function onUnsavedConfirm() {
  showUnsavedPrompt.value = false;
  // drop changes in editor and close
  editorRef.value.dropChanges();
  if (!props.alwaysOpen) toggleOpened(false);
  resumePendingOutsideEvent();
}

// stop the outside interaction from landing until the user answers the prompt
function suppressEvent(e) {
  e.preventDefault();
  e.stopPropagation();
  e.stopImmediatePropagation();
}

function bindGlobalOutsideGuards() {
  if (outsideGuard) return;
  outsideGuard = (e) => {
    // Only handle when our editor is opened
    if (!opened.value) return;
    // If alwaysOpen - ignore outside clicks entirely
    if (props.alwaysOpen) return;
    if (showUnsavedPrompt.value) return;
    const insideEditorInput = wrap.value && (wrap.value === e.target || wrap.value.contains(e.target));

    if (e.type === 'pointerdown') {
      // Record where the interaction STARTED
      pointerDownStartedOutside = !insideEditorInput;
      // If started outside and there are unsaved changes, prevent immediate focus change
      // and show the prompt right away. We'll resume the intended click after confirm.
      if (pointerDownStartedOutside && editorRef.value.hasUnsavedChanges()) {
        suppressEvent(e);
        if (!showUnsavedPrompt.value) {
          const cloned = clonePointerEvent(e);
          // Resume as a click instead of pointerdown for better UX
          cloned.type = 'click';
          pendingOutsideEvent = cloned;
          showUnsavedPrompt.value = true;
        }
      }
      return;
    }

    if (e.type === 'click') {
      // Only react to clicks that STARTED outside
      if (!pointerDownStartedOutside) return;

      if (editorRef.value.hasUnsavedChanges()) {
        suppressEvent(e);
        if (!showUnsavedPrompt.value) {
          pendingOutsideEvent = clonePointerEvent(e);
          showUnsavedPrompt.value = true;
        }
      } else {
        // No unsaved changes: allow close of editor
        if (!props.alwaysOpen) toggleOpened(false);
      }

      // Reset for next interaction
      pointerDownStartedOutside = false;
    }
  };
  document.addEventListener('pointerdown', outsideGuard, true);
  document.addEventListener('click', outsideGuard, true);
}

function unbindGlobalOutsideGuards() {
  if (!outsideGuard) return;
  document.removeEventListener('pointerdown', outsideGuard, true);
  document.removeEventListener('click', outsideGuard, true);
  outsideGuard = null;
}
</script>
<style lang='scss'>
[data-mode='light'] {
  @import 'highlight.js/styles/atom-one-light';
}
</style>
<style scoped lang='scss'>
/* Styles for the rendered (read-only) markup produced by the editor — it arrives
   as v-html, so it cannot carry utility classes. The editing view lives in Editor.vue. */

.multilineinput-wrapper :deep(code) {
  padding: 0 0.125rem;
  border: 0.0625rem solid color-mix(in srgb, var(--accent) 28%, transparent);
  border-radius: var(--chip-radius);
  background-color: var(--accent-soft);
  font-family: Consolas, monaco, monospace;
  line-height: 1;
}

.multilineinput-wrapper :deep(div.ql-code-block-container) {
  margin: 0.5rem 0;
  padding: 0.625rem;
  border: 0.0625rem solid var(--line);
  border-radius: var(--chip-radius);
  background-color: var(--input);
  color: var(--text);
  font-family: Consolas, monaco, monospace;
  font-size: var(--fs-body);

  & > code {
    border: 0;
    background-color: transparent;
    line-height: inherit;
  }
}

.title {
  :deep(p) {
    margin: 0;
    text-align: left;
  }

  :deep(ol),
  :deep(ul) {
    margin-block: 0;
    margin-inline: 0;
    padding-inline-start: 1.25rem;
  }

  /* Quill saves nested lists flat: the level is a `ql-indent-N` class on the item and the
     marker goes into an empty `.ql-ui` span, numbered per level with counters */
  :deep(li[data-list]) {
    list-style-type: none;
    counter-reset: list-1;
  }

  :deep(li[data-list] > .ql-ui::before) {
    display: inline-block;
    width: 1.25rem;
    margin-left: -1.25rem;
  }

  :deep(li[data-list='bullet'] > .ql-ui::before) {
    content: '\2022';
  }

  :deep(li[data-list='ordered']) {
    counter-increment: list-0;
  }

  :deep(li[data-list='ordered'] > .ql-ui::before) {
    content: counter(list-0) '.';
  }

  :deep(li.ql-indent-1) {
    padding-left: 1.25rem;
    counter-reset: list-2;
  }

  :deep(li[data-list='ordered'].ql-indent-1) {
    counter-increment: list-1;
  }

  :deep(li[data-list='ordered'].ql-indent-1 > .ql-ui::before) {
    content: counter(list-1, lower-alpha) '.';
  }

  :deep(li.ql-indent-2) {
    padding-left: 2.5rem;
    counter-reset: none;
  }

  :deep(li[data-list='ordered'].ql-indent-2) {
    counter-increment: list-2;
  }

  :deep(li[data-list='ordered'].ql-indent-2 > .ql-ui::before) {
    content: counter(list-2, lower-roman) '.';
  }

  :deep(blockquote) {
    margin: 0;
  }

  /* first and last blockquote of each consecutive group get outer breathing room */
  :deep(blockquote:not(blockquote + blockquote)) {
    margin-top: 0.375rem;
  }

  :deep(blockquote:not(:has(+ blockquote))) {
    margin-bottom: 0.375rem;
  }

  :deep(a) {
    color: var(--accent);
  }

  :deep(a:visited) {
    color: color-mix(in srgb, var(--accent) 75%, var(--base));
  }

  :deep(div.ql-code-block-container) {
    overflow-x: auto;
    white-space: pre;
  }

  :deep(.color-square) {
    display: inline-block;
    width: 0.625rem;
    height: 0.625rem;
    border: 0.0625rem solid var(--text);
    border-radius: 0.0625rem;
  }
}
</style>
